import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { LoadingService } from '../loading.service';
import { catchError, map, throwError, switchMap } from 'rxjs';
import { Injectable } from '@angular/core';
import { environment } from '@env/environment';
import {
  PassengerUpdateDocumentInt,
  RequestUpdateDocuments,
} from '@app/interfaces/requiredDocuments.interface';
import { ApiMapper } from '@app/mappers/micro/api.mapper';
import { Reservation } from '@app/models/micro/reservation.model';
import { PAX_TYPE, TypeLoading } from '@app/enums';
import { Passenger } from '@app/models/micro/passenger.model';
import { GlobalsService } from './globals.service';
import { DocumentsMapper } from '@app/mappers/micro/documents.mapper';
import { PassengerService } from './passenger.service';
import { ErrorAdapter } from '@models/adapter/error.adapter';
import { normalizeGender } from '@utils/functions/normalizeGender';
/**
 * Actualiza los documentos de un pasajero en una reserva.
 */
@Injectable({
  providedIn: 'root',
})
export class UpdateDocuments {
  passengerDocuments: PassengerUpdateDocumentInt;
  adult: any;
  // Cifrado (request y respuesta) por ApiEncryptionInterceptor; ver encrypted-services.config.
  urlMicroPNR = `${environment.urlApiCheckinBFF}${environment.versionCheckin}/kiosk/checkin${environment.versionUpdateDocuments}/updateDocuments`;
  // Cifrado (request y respuesta) por ApiEncryptionInterceptor; ver encrypted-services.config.
  urlPostCki = `${environment.urlApiCheckinBFF}${environment.versionCheckin}/kiosk/checkin/updateDocumentsSecurity`;
  constructor(
    private _loadingService: LoadingService,
    private _http: HttpClient,
    private _globalServ: GlobalsService,
    private _passengerService: PassengerService
  ) {}
  /**
   * Actualiza los documentos del pasajero en el sistema.
   * Envía el payload completo con información de pasaporte al backend.
   * @param reservation Reservación actual
   * @param passenger Pasajero a actualizar
   * @param document Tipo de documento
   * @param applyAllPassengers Si aplica a todos los pasajeros
   * @returns Observable con la respuesta del backend
   */
  updateDocuments(
    reservation: Reservation,
    passenger: Passenger,
    document: string,
    applyAllPassengers = false
  ) {
    if (this._globalServ.isFlowVeryPassport && document === 'passport') {
      const bookingReq = [this.getBookedLegCollection(reservation, true)];
      const tripType = reservation.flightItineraryType;
      const rqPassenger = this.passenderObj(passenger, document, reservation);
      const body = ApiMapper.requestUpdateDocuments(bookingReq, rqPassenger, reservation, tripType);
      const headers = this.getHeaders(reservation);

      return this._http.post<RequestUpdateDocuments>(this.urlMicroPNR, body, { headers }).pipe(
        switchMap((res) => {
          return this.updateDocumentsPostCki(reservation, passenger).pipe(
            map(() => res) // regresas el resultado del primer servicio si lo necesitas
          );
        }),
        map((res) => {
          this._loadingService.hideLoading(TypeLoading.FOOTER_LOADING);
          if (passenger?.passengerInfo?.passport) {
            passenger.passengerInfo.passport.passportScanned = true;
          }
          passenger.hasDOCV = true;
          passenger.missingCheckinOptionalFields = (
            passenger.missingCheckinOptionalFields || []
          ).filter((f) => f !== 'DOCV');
          this._globalServ.setPassenger(passenger);
          return DocumentsMapper.responseUpdateDocuments(res);
        }),
        catchError(this.handlerError.bind(this))
      );
    }

    const bookingReq = [this.getBookedLegCollection(reservation, true)];
    const tripType = reservation.flightItineraryType;
    const rqPassenger = applyAllPassengers
      ? this.applyAllPassengers(document, passenger, reservation)
      : this.passenderObj(passenger, document, reservation);
    const body = ApiMapper.requestUpdateDocuments(bookingReq, rqPassenger, reservation, tripType);

    const headers = this.getHeaders(reservation);
    this._loadingService.showLoading(TypeLoading.FOOTER_LOADING);

    return this._http.post<RequestUpdateDocuments>(this.urlMicroPNR, body, { headers }).pipe(
      map((res) => {
        this._loadingService.hideLoading(TypeLoading.FOOTER_LOADING);
        return DocumentsMapper.responseUpdateDocuments(res);
      }),
      catchError(this.handlerError.bind(this))
    );
  }
  /**
   * CI-23290: asienta la atestacion CDC (SSR ADOC + ATOC) de un pasajero.
   * Si el pasajero lleva infante, se manda tambien el infante: BE le asienta su propio SSR.
   * @param reservation Reservacion actual
   * @param passenger Adulto o menor que envio "No" en la declaracion
   * @returns Observable con la respuesta del backend (fieldsUpdate / fieldsFailed por pasajero)
   */
  updateAttestation(reservation: Reservation, passenger: Passenger) {
    const infant = passenger?.passengerInfant
      ? reservation.passengers.find((p) => p.nameRefNumber === passenger.passengerInfant)
      : null;
    // passenderObj con un infante arma [infante, adulto con passengerInfant], igual que los demas documentos.
    const rqPassenger = this.passenderObj(infant ?? passenger, 'attestation', reservation).map(
      (rq) => ({ ...rq, attestation: { addADOC: true, addATOC: true } })
    );
    const bookingReq = [this.getBookedLegCollection(reservation, true)];
    const body = ApiMapper.requestUpdateDocuments(
      bookingReq,
      rqPassenger,
      reservation,
      reservation.flightItineraryType
    );
    const headers = this.getHeaders(reservation);

    return this._http.post<RequestUpdateDocuments>(this.urlMicroPNR, body, { headers }).pipe(
      map((res) => DocumentsMapper.responseUpdateDocuments(res)),
      catchError(this.handlerError.bind(this))
    );
  }
  /**
   * Maneja errores de la solicitud HTTP.
   * @param error El error recibido en la respuesta HTTP.
   * @return Lanza un error procesado.
   */
  handlerError(error: HttpErrorResponse) {
    this._loadingService.hideLoading(TypeLoading.FOOTER_LOADING);
    const errorAdapt = ErrorAdapter.adapt(error?.error);
    return throwError(errorAdapt);
  }
  /**
   * Obtiene el tipo de pasajero.
   * @param passenger El pasajero del cual obtener el tipo.
   * @return Tipo de pasajero como string.
   */
  getPaxType(passenger) {
    let paxType = '';
    if (passenger?.paxType == PAX_TYPE.ADULT) {
      paxType = 'ADT';
    } else if (passenger?.paxType == PAX_TYPE.CHILD) {
      paxType = 'CHD';
    } else if (passenger?.paxType == PAX_TYPE.INFANT) {
      paxType = 'INF';
    }
    return paxType;
  }

  getGender(passenger: Passenger) {
    let gender = null;
    if (passenger.passengerInfo.gender.length > 0) {
      gender = null;
    } else {
      gender = passenger.passengerInfo.gender == 'MALE' ? 'H' : 'M';
    }
    return gender;
  }

  getBirthDay(passenger: Passenger) {
    let birthDay = null;
    if (passenger.passengerInfo.dateOfBirth.length > 0) {
      birthDay = null;
    } else {
      birthDay = passenger?.passengerInfo?.dateOfBirth;
    }
    return birthDay;
  }
  /**
   * Obtiene la colección de segmentos de vuelo de una reserva.
   * @param reservation La reserva a evaluar.
   * @param selectedLeg Indica si se selecciona un segmento específico.
   * @return Objeto con los segmentos de vuelo.
   */
  getBookedLegCollection(reservation: Reservation, selectedLeg = false) {
    return {
      codeShare: reservation?.codeShare,
      legCode: reservation?.flightSummary?.legCode,
      legEntity: reservation?.flightSummary?.legEntity,
      legRegion: reservation?.flightSummary?.legRegion,
      segments: [
        {
          bookingClass: reservation?.flightSummary?.currentSegment?.bookingClass,
          departureAirport: reservation?.flightSummary?.currentSegment?.departureAirport,
          departureDateTime: reservation?.flightSummary?.currentSegment?.departureDateTime,
          operatingCarrier: reservation?.flightSummary?.currentSegment?.operatingCarrier,
          operatingFlightCode: reservation?.flightSummary?.currentSegment?.operatingFlightCode,
          segmentCode: reservation?.flightSummary?.currentSegment?.segmentCode,
          windowSegmentStatus:
            reservation?.flightSummary?.currentSegment?.windowSegmentStatus || null,
        },
      ],
      selectedLeg: selectedLeg,
      sdsProvider: reservation?.flightSummary?.sdsProvider,
    };
  }

  /**
   * Construye el objeto de pasaporte para enviar al backend.
   * Implementa estrategia de fallback: prioriza datos escaneados sobre datos del PNR.
   * Incluye los campos requeridos por CBP: firstName, lastName, gender, birthDate.
   * @param passenger Pasajero con información de pasaporte
   * @returns Objeto de pasaporte con 8 campos o null si la validación falla
   */
  buildPassport(passenger: Passenger) {
    // CI-17097: Construcción de payload con campos adicionales
    // Estrategia: Priorizar datos escaneados (passportInfo) sobre datos del PNR (passenger)
    const passportInfo = passenger?.passengerInfo?.passport;
    const mappedGender =
      passenger.passengerInfo?.gender?.toUpperCase() === 'MALE'
        ? 'M'
        : passenger.passengerInfo?.gender?.toUpperCase() === 'FEMALE'
        ? 'F'
        : 'U';

    const objPassport = {
      firstName: passportInfo?.firstName || passenger?.firstName,
      lastName: passportInfo?.lastName || passenger?.lastName,
      birthDate: passportInfo?.birthDate || passenger.passengerInfo?.dateOfBirth,
      gender: normalizeGender(passportInfo?.gender || mappedGender),
      expirationDate: passportInfo?.expirationDate,
      issueCountry: passportInfo?.issuingCountry,
      nationality: passportInfo?.nationality,
      number: passportInfo?.documentNumber,
      passportScanned: passportInfo?.passportScanned,
    };

    const isValidDynamic = this.validatePassportInfoDynamic(objPassport);

    return isValidDynamic ? objPassport : null;
  }

  /**
   * Formatea una fecha ISO (YYYY-MM-DD) al formato requerido por Sabre (DDMMMYYYY)
   * Ejemplo: "1990-04-22" -> "22APR1990"
   * @param isoDate Fecha en formato ISO (YYYY-MM-DD)
   * @returns Fecha en formato Sabre (DDMMMYYYY) o string vacío si la fecha es inválida
   */
  private formatDateForSabre(isoDate: string): string {
    if (!isoDate) return '';

    const months = [
      'JAN',
      'FEB',
      'MAR',
      'APR',
      'MAY',
      'JUN',
      'JUL',
      'AUG',
      'SEP',
      'OCT',
      'NOV',
      'DEC',
    ];

    const [year, month, day] = isoDate.split('-');

    if (!year || !month || !day) {
      console.error('[PASSPORT] Invalid date format:', isoDate);
      return '';
    }

    const monthIndex = parseInt(month) - 1;
    return `${day}${months[monthIndex]}${year}`;
  }

  buildPhone(passenger: Passenger) {
    // CVT20-14571: `phones` puede llegar como objeto {countryCode, number} (lo que
    // guarda el form del adulto) o como array [{countryCode, number}] (formato del
    // PNR / el que deja assignInfantMissingInfo en el INF). Se normaliza el acceso
    // para que el INF herede el telefono con codigo de pais sin romper el caso ADT.
    const phones = passenger.passengerInfo?.phones;
    const phoneData = Array.isArray(phones) ? phones[0] : phones;
    let phone = {
      countryCode: phoneData?.countryCode?.replace('+', ''),
      number: phoneData?.number,
      type: 'mobile',
    };
    const isValidDynamic = this.validatePassportInfoDynamic(phone);
    return isValidDynamic ? [phone] : null;
  }

  validatePassportInfoDynamic(info: any): boolean {
    // Validar que todas las propiedades del objeto tengan un valor
    return Object.values(info).every(
      (value) => value !== null && value !== undefined && value !== ''
    );
  }
  validateObjectFields(obj) {
    if (obj == null) return null;
    return Object.values(obj).every((value) => {
      // Define las condiciones para campos "vacíos"
      if (
        value === null ||
        value === undefined ||
        value === '' ||
        (Array.isArray(value) && value.length === 0) ||
        (typeof value === 'object' && Object.keys(value).length === 0)
      ) {
        return false; // Campo vacío
      }
      return true; // Campo válido
    });
  }
  passenderObj(passenger: Passenger, document: string, reservation: Reservation) {
    const destinationAddress =
      document == 'destinationAddress' ? passenger.passengerInfo.destinationAddress : null;
    const email = document == 'contactInformation' ? passenger.passengerInfo.emails : null;
    const emergencyContact =
      document == 'emergencyContact' ? passenger.passengerInfo.emergencyContact : null;
    // CI-23050: enviar ffNumber/ffProgram solo si este pasajero modifico su FFN
    // (passenger.updateFFN). Reenviar un FFN ya existente en el PNR provoca
    // "FQTV DATA EXISTS FOR THIS PASSENGER" en Sabre y falla el update.
    // Ambos campos van acoplados: no se envia uno sin el otro.
    const hasNewFfn =
      passenger.updateFFN === true &&
      (passenger.passengerInfo.ffNumber || '') !== '' &&
      (passenger.passengerInfo.ffProgram || '') !== '';
    const ffNumber =
      document === 'contactInformation' && hasNewFfn ? passenger.passengerInfo.ffNumber : null;
    const ffProgram =
      document === 'contactInformation' && hasNewFfn ? passenger.passengerInfo.ffProgram : null;
    const passport = document == 'passport' ? this.buildPassport(passenger) : null;
    const phoneContact = document == 'contactInformation' ? this.buildPhone(passenger) : null;
    const visa = document == 'visa' ? passenger.passengerInfo.visa : null;
    const residence = document == 'residence' ? passenger.passengerInfo.residenceAddress : null;
    const isPassportScanUpdate =
      passenger?.passengerInfo?.passport?.passportScanned === true &&
      document === 'passport' &&
      passport !== null;
    const isManualPassportUpdate =
      passenger?.passengerInfo?.passport?.passportScanned === false &&
      document === 'passport' &&
      passport !== null;
    const isPassportFlow = isPassportScanUpdate || isManualPassportUpdate;
    // CI-24166: en INT el DOB y genero solo viajan en el pasaporte; enviarlos sueltos
    // genera el warning "DOB ALREADY COLLECTED, DB IS NOT REQUIRED"
    const isInternational = reservation?.flightSummary?.isDomestic === false;
    const birthDateValue =
      isPassportFlow || isInternational ? null : passenger.passengerInfo.dateOfBirth || null;
    const gender = birthDateValue ? normalizeGender(passenger?.passengerInfo?.gender) : null;

    const timaticRQ = document == 'timaticRQ' ? passenger.passengerInfo.timaticRQ : null;
    const ktn =
      document == 'contactInformation' &&
      this.validateObjectFields(passenger?.passengerInfo?.travelerNumber)
        ? passenger?.passengerInfo?.travelerNumber
        : null;
    const permanentResidentCard =
      document == 'permanentResidentCard' ? passenger?.passengerInfo?.permanentResidentCard : null;
    const redress =
      document == 'contactInformation' &&
      this.validateObjectFields(passenger?.passengerInfo?.redressNumber)
        ? passenger?.passengerInfo?.redressNumber
        : null;
    const contactTracing = passenger?.contactTracing;
    if (passenger.paxType == PAX_TYPE.INFANT) {
      this.adult = reservation.passengers.find((x) => x.passengerInfant == passenger.nameRefNumber);
    }
    let request = [];

    const passengerRequest: any = {
      permanentResidentCard: permanentResidentCard,
      countryOfResidence:
        reservation.codeShare && (document == 'passport' || document == 'emergencyContact')
          ? passenger.passengerInfo.countryResidence
          : null,
      ktn: ktn,
      redress: redress,
      birthDate: birthDateValue,
      destinationAddress: destinationAddress,
      email: email,
      emergencyContact: emergencyContact,
      ffNumber: ffNumber,
      ffProgram: ffProgram,
      firstName: passenger?.firstName,
      gender: gender,
      eticket: passenger.ticketInfo?.eticket,
      lastName: passenger.lastName,
      passengerIds:
        passenger.paxType == PAX_TYPE.INFANT ? this.adult?.passengerIds : passenger?.passengerIds,
      passengerNameNumber: passenger?.nameRefNumber,
      passport: passport,
      phone: phoneContact,
      selectedForCheckin: true,
      type: this.getPaxType(passenger),
      visa: visa,
      timaticRQ: timaticRQ,
      contactTracing: contactTracing, //solo para reservas a usa o (PASSENGER_TRACING_CONTACT_PHONE","PASSENGER_TRACING_CONTACT_EMAIL)
      passengerIdSkyteam: reservation.codeShare ? passenger?.passengerIdSkyteam : '',
    };
    if (!reservation.codeShare && (document == 'passport' || document == 'emergencyContact')) {
      passengerRequest.residenceAddress = this.objResidence(
        passenger.passengerInfo.countryResidence
      );
    }
    request.push(passengerRequest);

    if (passenger.paxType == PAX_TYPE.INFANT) {
      ///se anexa el adulto al que pertenece el infante
      const infantRequest: any = {
        countryOfResidence: null,
        ktn: null,
        redress: null,
        birthDate: isInternational ? null : this.adult.passengerInfo.dateOfBirth,
        destinationAddress: null,
        timaticRQ: null,
        email: null,
        emergencyContact: null,
        ffNumber: null,
        ffProgram: null,
        firstName: this.adult?.firstName,
        gender:
          !isInternational && this.adult.passengerInfo.dateOfBirth
            ? normalizeGender(this.adult?.passengerInfo?.gender)
            : null,
        eticket: passenger.ticketInfo?.eticket,
        lastName: this.adult.lastName,
        passengerIds: this.adult.passengerIds,
        passengerNameNumber: this.adult?.nameRefNumber,
        passport: null,
        permanentResidentCard: null,
        phone: null,
        selectedForCheckin: true,
        type: 'ADT',
        visa: null,
        residenceAddress: null,
        passengerInfant: passenger?.nameRefNumber,
        contactTracing: contactTracing,
        passengerIdSkyteam: reservation.codeShare ? this.adult.passengerIdSkyteam : '',
      };
      request.push(infantRequest);
    }
    return request;
  }

  applyAllPassengers(document, passenger: Passenger, reservation: Reservation) {
    const allPassengers = this._passengerService.getPassengerSelection(true);
    const timaticRQ = document == 'timaticRQ' ? passenger.passengerInfo.timaticRQ : null;
    const emergencyContact =
      document == 'emergencyContact' ? passenger.passengerInfo.emergencyContact : null;
    const destinationAddress =
      document == 'destinationAddress' ? passenger.passengerInfo.destinationAddress : null;
    const contactTracing = passenger?.contactTracing || null;
    const value =
      document == 'emergencyContact'
        ? passenger.passengerInfo.emergencyContact
        : document == 'destinationAddress'
        ? passenger.passengerInfo.destinationAddress
        : passenger.passengerInfo.timaticRQ;
    const requestAllPassengers = [];
    if (passenger.paxType == PAX_TYPE.INFANT) {
      this.adult = reservation.passengers.find((x) => x.passengerInfant == passenger.nameRefNumber);
    }
    for (const element of allPassengers) {
      element.passengerInfo[document] = value;
      this._globalServ.setPassenger(element);
      const rq: any = {
        countryOfResidence:
          reservation.codeShare && document == 'emergencyContact'
            ? element.passengerInfo.countryResidence
            : null,
        ktn: null,
        redress: null,
        birthDate: null,
        destinationAddress: destinationAddress,
        email: null,
        emergencyContact: emergencyContact,
        ffNumber: null,
        ffProgram: null,
        firstName: element.firstName,
        gender: null,
        eticket: element.ticketInfo?.eticket,
        lastName: element.lastName,
        passengerIds:
          element.paxType == PAX_TYPE.INFANT ? this.adult?.passengerIds : element?.passengerIds,
        passengerNameNumber: element.nameRefNumber,
        passport: null,
        timaticRQ: timaticRQ,
        permanentResidentCard: null,
        phone: null,
        selectedForCheckin: true,
        type: this.getPaxType(element),
        passengerInfant: element?.passengerInfant !== '' ? element?.passengerInfant : undefined,
        //"residenceAddress": !reservation.codeShare &&  document == 'emergencyContact'  ? this.objResidence(passenger.passengerInfo.countryResidence): null,
        contactTracing: contactTracing,
        passengerIdSkyteam: reservation.codeShare ? element?.passengerIdSkyteam : '',
      };
      if (!reservation.codeShare && document == 'emergencyContact') {
        rq.residenceAddress = this.objResidence(passenger.passengerInfo.countryResidence);
      }

      requestAllPassengers.push(rq);
    }
    return requestAllPassengers;
  }

  objResidence(countryResidence) {
    return countryResidence
      ? {
          country: countryResidence,
        }
      : null;
  }

  buildPostCkiRequest(reservation: Reservation, passenger: Passenger) {
    const segment = reservation.flightSummary.currentSegment;
    const passengerId =
      passenger.passengerIds?.length > 0 ? passenger.passengerIds[0].id : passenger.passengerId;
    return {
      reservationCode: reservation.pnr,
      segment: {
        bookingClass: segment.bookingClass,
        departureAirport: segment.departureAirport,
        departureDateTime: segment.departureDateTime,
        operatingCarrier: segment.operatingCarrier,
        operatingFlightCode: segment.operatingFlightCode,
        segmentCode: segment.segmentCode,
        segmentStatusCode: segment.segmentStatusCode,
      },
      passengers: [
        {
          passengerNameNumber: passenger.nameRefNumber,
          passengerId: passengerId,
          firstName: passenger.firstName,
          lastName: passenger.lastName,
          addDOCV: true,
          type: this.getPaxType(passenger),
        },
      ],
    };
  }

  updateDocumentsPostCki(reservation: Reservation, passenger: Passenger) {
    const body = this.buildPostCkiRequest(reservation, passenger);
    const headers = this.getHeaders(reservation);
    this._loadingService.showLoading(TypeLoading.FOOTER_LOADING);
    return this._http.post<any>(this.urlPostCki, body, { headers }).pipe(
      map((res) => {
        this._loadingService.hideLoading(TypeLoading.FOOTER_LOADING);
        return DocumentsMapper.responseUpdateDocuments(res);
      }),
      catchError(this.handlerError.bind(this))
    );
  }

  private getHeaders(reservation: Reservation): HttpHeaders {
    return new HttpHeaders({
      channel: 'KIOSK',
      flow: 'checkin',
      'x-transaction-id': reservation?.flightSummary?.cartId,
      'Content-Type': 'application/json',
    });
  }
}
